import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from './api';
import { useAuth } from '../state/AuthContext';
import { useT } from '../i18n';

// ---------------------------------------------------------------------------
// The ONE page-data hook. Conv-49.
// ---------------------------------------------------------------------------
// There were TWELVE near-identical copies of this across `src/pages/app/*`, and
// Planning's copy carried a comment naming the situation precisely: "unify the
// twelve, which is its own reviewed change". The Conv-49 nav restructure IS that
// change — a section cannot move from one page to another while the hook it
// closes over is a file-local with a different signature on the far side, so the
// twelve copies were the thing physically preventing the re-homing.
//
// This is Planning's version, because it was the only STRICTLY CORRECT one:
//
//  • THE STALE-RESPONSE GUARD IS KEPT, AND IT IS NOT OPTIONAL HERE. Responses can
//    land out of order, so a slow request for NIFTY50 could overwrite a fast one
//    for BANKNIFTY and leave the cards showing one index's returns under another
//    index's name. Only the newest request may write state. It mattered on
//    exactly one page before (the benchmark, whose `path` changes with user
//    input) — but a single shared hook is used by every future dynamic-path
//    caller too, and the guard costs one integer.
//
//  • `t` IS DELIBERATELY NOT IN THE DEP ARRAY, and three of the twelve copies had
//    it (Trackers, Community, Privacy). That is the Conv-30 class: `t` is
//    referenced only inside `.catch`, so putting it in `load`'s deps makes a
//    locale switch re-fire a real API GET. It was survivable only because
//    `useT()`'s `t` never changes identity — i.e. those three were relying on an
//    invariant of a different module. The shared version does not rely on it.
//
//  • `reload` is an ALIAS for `load`, not a second function. Four of the copies
//    named it `reload` and the rest named it `load`; exporting both names lets
//    every existing call site move without a rename, and aliasing (rather than
//    wrapping) keeps them referentially identical so a dep array cannot tell
//    them apart.
//
// `auto` keeps its meaning: false means "do not fetch on mount", used by the
// forms that must not ask the server anything until the user has supplied the
// assumptions (projection, FIRE) — an auto-fetch there would make Sansara pick
// the number, which is the Batch-10 60/40 bug.
export function useApi<T>(path: string, auto = true) {
  const t = useT();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(auto);
  const seq = useRef(0);
  const load = useCallback((p?: string) => {
    const mine = ++seq.current;
    setLoading(true);
    apiGet<T>(p ?? path)
      .then((d) => { if (mine === seq.current) { setData(d); setError(null); } })
      .catch((e) => { if (mine === seq.current) setError(e instanceof Error ? e.message : t('Failed to load')); })
      .finally(() => { if (mine === seq.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` is read only in
    // the catch; including it re-fires a real GET on every locale switch (Conv-30).
  }, [path]);
  // Drop a stale answer AND any in-flight response for it. Bumping `seq` matters:
  // without it a request already on the wire would land after the reset and put
  // the old figure back. Used by FireSection, where a result that outlives its
  // own inputs is a number attributed to assumptions nobody can see.
  const reset = useCallback(() => { seq.current += 1; setData(null); setError(null); setLoading(false); }, []);
  useEffect(() => { if (auto) load(); }, [auto, load]);
  return { data, error, loading, load, reload: load, reset };
}

export function useMutate() {
  const { csrfToken } = useAuth();
  return useCallback(
    <T,>(path: string, body: Record<string, unknown> = {}) => apiPost<T>(path, { csrfToken, ...body }),
    [csrfToken],
  );
}
